import type { Id } from "@convex/_generated/dataModel";
import type { UseMutationResult } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";

import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import type { EmployeeReportSettings } from "./employee-report-types";
import { toPrinterDeviceId } from "./employee-report-types";

interface Printer {
  _id: string;
  name: string;
}

interface EmployeeReportSettingsFieldsProps {
  form: UseFormReturn<EmployeeReportSettings>;
  handleToggleChange: (
    name: keyof EmployeeReportSettings,
    checked: boolean
  ) => void;
  printers: Printer[] | undefined;
  locationId: string;
  updateSettingsMutation: UseMutationResult<
    unknown,
    Error,
    {
      locationId: Id<"locations">;
      value: {
        showOrdersAndBillsSummary: boolean;
        showSalesCategoriesSummary: boolean;
        showDevicesSummary: boolean;
        showVoidedItems: boolean;
        showDiscountedItems: boolean;
        showRefundedItems: boolean;
        showManagerCodes: boolean;
        showEmployeeHours: boolean;
        showEmployeeTips: boolean;
        reprintEmployeeReportWhenClockingOut: boolean;
        showDividers: boolean;
        printerDeviceId: "all" | Id<"printers">;
        printAutomaticallyOnClockOut: boolean;
        printCopies: number;
      };
    }
  >;
}

export function EmployeeReportSettingsFields({
  form,
  handleToggleChange,
  printers,
  locationId,
  updateSettingsMutation,
}: EmployeeReportSettingsFieldsProps) {
  return (
    <Form {...form}>
      <form className="space-y-4">
        <div>
          <h3 className="text-foreground py-5 font-medium text-balance">
            Report Information
            <p className="body-sm text-muted-foreground">
              Configure which sections to show in the employee report.
            </p>
          </h3>
          <hr className="bg-border mb-4 h-px border-none" />
        </div>

        <FormField
          control={form.control}
          name="showEmployeeHours"
          render={() => (
            <FormItem>
              <FormLabel className="text-foreground flex items-center gap-2">
                <FormControl>
                  <Lock className="stroke-muted-foreground size-6" />
                </FormControl>
                Show employee hours
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="showEmployeeTips"
          render={() => (
            <FormItem>
              <FormLabel className="text-foreground flex items-center gap-2">
                <FormControl>
                  <Lock className="stroke-muted-foreground size-6" />
                </FormControl>
                Show employee tips
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="showOrdersAndBillsSummary"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      handleToggleChange("showOrdersAndBillsSummary", checked);
                    }}
                  />
                </FormControl>
                Show orders and bills summary
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="showSalesCategoriesSummary"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      handleToggleChange("showSalesCategoriesSummary", checked);
                    }}
                  />
                </FormControl>
                Show sales categories summary
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="showDevicesSummary"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      handleToggleChange("showDevicesSummary", checked);
                    }}
                  />
                </FormControl>
                Show devices summary
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="showVoidedItems"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      handleToggleChange("showVoidedItems", checked);
                    }}
                  />
                </FormControl>
                Show voided items
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="showDiscountedItems"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      handleToggleChange("showDiscountedItems", checked);
                    }}
                  />
                </FormControl>
                Show discounted items
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="showRefundedItems"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      handleToggleChange("showRefundedItems", checked);
                    }}
                  />
                </FormControl>
                Show refunded items
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="showManagerCodes"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      handleToggleChange("showManagerCodes", checked);
                    }}
                  />
                </FormControl>
                Show permission codes
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />

        <div>
          <h3 className="text-foreground py-5 font-medium text-balance">
            Print Settings
          </h3>
          <hr className="bg-border mb-4 h-px border-none" />
        </div>

        <FormField
          control={form.control}
          name="printerDeviceId"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground">
                Select printer device
              </FormLabel>
              <FormControl>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    updateSettingsMutation
                      .mutateAsync({
                        locationId: toConvexTableId("locations", locationId),
                        value: {
                          ...form.getValues(),
                          printerDeviceId: toPrinterDeviceId(value),
                        },
                      })
                      .catch((error: Error) => {
                        logger.exception(
                          "employee_report_settings_update_failed",
                          error
                        );
                      });
                  }}
                  value={field.value ?? "all"}
                >
                  <SelectTrigger className="w-fit min-w-40">
                    <SelectValue placeholder="Select a printer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Available Printer</SelectItem>
                    {printers?.map((printer) => (
                      <SelectItem key={printer._id} value={printer._id}>
                        {printer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <p className="body-sm text-muted-foreground ml-12">
                Choose which printer device to use for printing the employee
                report. Select "Any Available Printer" to allow printing on any
                configured printer.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="printAutomaticallyOnClockOut"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      handleToggleChange(
                        "printAutomaticallyOnClockOut",
                        checked
                      );
                    }}
                  />
                </FormControl>
                Print automatically on clock out
              </FormLabel>
              <p className="body-sm text-muted-foreground ml-12">
                When an employee clocks out, their report will be automatically
                printed.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="showDividers"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-foreground flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      handleToggleChange("showDividers", checked);
                    }}
                  />
                </FormControl>
                Show dividers
              </FormLabel>
              <p className="body-sm text-muted-foreground ml-12">
                Sections will be visually separated by dashed lines.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
